import { Alert, Badge, Button, Field, Input } from '@personachat/ui';
import { CalendarClock } from 'lucide-react';
import { useEffect, useId, useState, type FormEvent } from 'react';

import { api } from '../../api/client';
import { lire, messageDe } from '../../api/call';
import type { components } from '../../api/schema';
import { useSession } from '../../auth/AuthProvider';
import { InfoList, Section } from '../../components/bits';
import { Modal } from '../../components/Modal';
import { useToast } from '../../components/Toast';
import { bornesJourParis, formatCredits, formatDate, jourParis } from '../../lib/format';
import { LIBELLE_PERIODE, STATUT_ACCES, STATUT_RENOUVELLEMENT } from '../../lib/labels';

export type Acces = components['schemas']['AccessAdmin'];

/** Pastille de l'état d'accès (fiche et liste). */
export function AccesBadge({ acces }: { acces: Pick<Acces, 'status' | 'endsAt'> }) {
  const s = STATUT_ACCES[acces.status];
  if (acces.status === 'NONE') return null;
  const jusqua = acces.status === 'ACTIVE' || acces.status === 'EXTENDED' ? ` jusqu’au ${formatDate(acces.endsAt)}` : '';
  return <Badge tone={s.ton}>{`${s.libelle}${jusqua}`}</Badge>;
}

/**
 * Section « Accès » de la fiche : activation (accès complet), fin de période,
 * fin prévue, renouvellements, pause, rappels. Un super admin corrige les dates.
 */
export function AccesSection({
  eleveId,
  userType,
  isAdmin,
  acces,
  onFait,
}: {
  eleveId: string;
  userType: string;
  isAdmin: boolean;
  acces: Acces;
  onFait: () => Promise<void>;
}) {
  const { user: admin } = useSession();
  const [modifier, setModifier] = useState(false);
  if (isAdmin) return null;

  const normal = userType === 'NORMAL';
  const peutModifier = !!admin?.isSuperAdmin && normal;

  return (
    <Section title="Accès">
      {acces.status === 'NONE' ? (
        <p className="note">
          {normal
            ? 'Pas encore de dates d’accès : le script d’initialisation n’a pas été lancé pour ce compte.'
            : 'Compte Invité ou Immersion : l’accès d’un an commence au passage en accès complet.'}
        </p>
      ) : (
        <InfoList
          items={[
            { label: 'État', value: <AccesBadge acces={acces} /> },
            { label: 'Accès complet depuis', value: formatDate(acces.startedAt) },
            { label: 'Période en cours', value: acces.period ? LIBELLE_PERIODE[acces.period] : '—', hidden: acces.status === 'ENDED' },
            {
              label: acces.status === 'ENDED' ? 'Accès terminé le' : 'Fin de la période',
              value: (
                <span>
                  {formatDate(acces.periodEndsAt)}
                  {acces.extensionIfCreditsLeft && acces.status !== 'ENDED' && (
                    <span className="note"> · 3 mois de plus s’il reste des crédits à cette date</span>
                  )}
                </span>
              ),
            },
            {
              label: 'Fin d’accès prévue',
              value: formatDate(acces.endsAt),
              hidden: acces.status === 'ENDED' || acces.endsAt === acces.periodEndsAt,
            },
            {
              label: 'En pause depuis',
              value: (
                <span>
                  {formatDate(acces.pausedAt)} <span className="note">· la fin sera repoussée d’autant à la réactivation</span>
                </span>
              ),
              hidden: !acces.pausedAt,
            },
            {
              label: 'Rappels envoyés',
              value: [acces.reminder30For && 'J-30', acces.reminder7For && 'J-7'].filter(Boolean).join(', ') || 'Aucun pour l’instant',
            },
          ]}
        />
      )}

      {acces.renewals.length > 0 && (
        <ul className="plain-list">
          {acces.renewals.map((r) => {
            const s = STATUT_RENOUVELLEMENT[r.status];
            return (
              <li key={r.id}>
                <Badge tone={s.ton}>{s.libelle}</Badge> <strong>{r.packName ?? `Renouvellement ${r.months} mois`}</strong>
                <span className="muted small">
                  {' '}
                  · {formatCredits(r.credits)} · payé le {formatDate(r.createdAt)}
                  {r.startsAt && ` · du ${formatDate(r.startsAt)} au ${formatDate(r.endsAt)}`}
                </span>
              </li>
            );
          })}
        </ul>
      )}

      {peutModifier && (
        <div>
          <Button icon={<CalendarClock size={15} aria-hidden="true" />} onClick={() => setModifier(true)}>
            Modifier les dates d’accès
          </Button>
        </div>
      )}

      <DatesAccesDialog open={modifier} onOpenChange={setModifier} eleveId={eleveId} acces={acces} onFait={onFait} />
    </Section>
  );
}

/** Correction des dates (super admin, motif obligatoire). */
function DatesAccesDialog({
  open,
  onOpenChange,
  eleveId,
  acces,
  onFait,
}: {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  eleveId: string;
  acces: Acces;
  onFait: () => Promise<void>;
}) {
  const id = useId();
  const toast = useToast();
  const [debut, setDebut] = useState('');
  const [fin, setFin] = useState('');
  const [motif, setMotif] = useState('');
  const [erreur, setErreur] = useState<string | null>(null);
  const [envoi, setEnvoi] = useState(false);

  const debutInitial = acces.startedAt ? jourParis(new Date(acces.startedAt)) : '';
  const finInitiale = acces.periodEndsAt ? jourParis(new Date(acces.periodEndsAt)) : '';

  useEffect(() => {
    if (open) {
      setDebut(debutInitial);
      setFin(finInitiale);
      setMotif('');
      setErreur(null);
    }
  }, [open, debutInitial, finInitiale]);

  const debutChange = debut !== debutInitial && debut !== '';
  const finChange = fin !== finInitiale && fin !== '';

  const valider = async (e: FormEvent) => {
    e.preventDefault();
    setEnvoi(true);
    setErreur(null);
    try {
      await lire(
        api.PATCH('/api/admin/users/{id}/access', {
          params: { path: { id: eleveId } },
          body: {
            ...(debutChange ? { startedAt: bornesJourParis(debut, 'debut') } : {}),
            ...(finChange ? { endsAt: bornesJourParis(fin, 'fin') } : {}),
            motif: motif.trim(),
          },
        })
      );
      await onFait();
      toast.succes('Dates d’accès modifiées.');
      onOpenChange(false);
    } catch (err) {
      setErreur(messageDe(err));
    } finally {
      setEnvoi(false);
    }
  };

  return (
    <Modal open={open} onOpenChange={(o) => !envoi && onOpenChange(o)} title="Modifier les dates d’accès">
      <form className="stack" onSubmit={valider}>
        <Field
          id={`${id}-debut`}
          label="Accès complet depuis"
          hint="Jour de l’activation avec une clé d’accès complet. Sans renouvellement commencé, la fin de la première année est recalculée (12 mois, 15 pour une activation avant 2026)."
        >
          <Input id={`${id}-debut`} type="date" value={debut} onChange={(e) => setDebut(e.target.value)} />
        </Field>
        <Field
          id={`${id}-fin`}
          label="Fin de la période en cours"
          hint="Dernier jour d’accès. En première année, s’il reste des crédits à cette date, 3 mois de plus s’ajoutent. Un accès terminé rouvert devient une prolongation : le solde est perdu à la fin."
        >
          <Input id={`${id}-fin`} type="date" value={fin} onChange={(e) => setFin(e.target.value)} disabled={debutChange} />
        </Field>
        {debutChange && <p className="note">La fin sera recalculée depuis la nouvelle date d’activation.</p>}
        <Field id={`${id}-motif`} label="Motif" hint="Obligatoire, inscrit au journal d’audit.">
          <Input
            id={`${id}-motif`}
            value={motif}
            onChange={(e) => setMotif(e.target.value)}
            maxLength={300}
            placeholder="Ex. : activée le 05/10 au passage en accès complet"
          />
        </Field>
        {erreur && <Alert>{erreur}</Alert>}
        <div className="modal__footer modal__footer--inline">
          <Button onClick={() => onOpenChange(false)} disabled={envoi}>
            Annuler
          </Button>
          <Button type="submit" variant="primary" loading={envoi} disabled={motif.trim().length < 3 || (!debutChange && !finChange)}>
            Enregistrer
          </Button>
        </div>
      </form>
    </Modal>
  );
}
